<p>This document was last updated on 25 July 2023.</p>

<p>
  This document sets out the terms and conditions (“Terms and Conditions”) which apply to all persons and legal entities
  who visit and use the services provided by The Vintage Bar ApS (“Users”), company registration no. 39106744, with the
  registered address Prags Boulevard 49A 2300 Copenhagen, Denmark (together with any of its subsidiaries “TVB”), the
  owner and administrator of
  <a href="www.thevintagebar.com" class="link primary">www.thevintagebar.com</a>
  (the “Platform” and the “TVB Services”).
</p>
<p>
  The Terms and Conditions are a legally binding contract between you and TVB (i.e. The Vintage Bar ApS. In here you
  will find the rights and obligations applicable to you as a User and TVB. By using any of the TVB Services you agree
  to the Terms and Conditions. If you do not agree to the Terms and Conditions you may not use any of the TVB Services.
  If you have any questions about the Terms and Conditions please reach out:
  <a href="mailto:contact@thevintagebar.com" class="link primary">contact@thevintagebar.com</a>
  .
</p>
<p>The Terms and Conditions include the following:</p>
<ol>
  <li>General Terms of Use (applicable to all Users using TVB Services)</li>
  <li>Sellers’ Terms (applicable to all Users selling Products via the Platform)</li>
  <li>Buyers’ Terms (applicable to all Users buying Products via the Platform)</li>
</ol>

<h2>1 General Terms of Use</h2>
<p>
  As a User you may browse and create an account on the Platform through which a User may (i) produce content (“User
  Content”), (ii) market and sell second-hand fashion products (the “Products”) to other Users, and (iii) buy products
  from other Users (and in some cases TVB). This Section 1 applies to all Users. If you intend to market and sell
  Products it is important that you also read Section 2 and if you intend to buy Products it is important that you also
  read Section 3.
</p>
<p>1.1 The Use of TVB Services</p>
<p>1.2 Having an Account with TVB</p>
<p>1.3 User Content</p>
<p>1.4 Privacy</p>
<p>1.5 TVB’s Responsibilities towards Users</p>
<p>1.6 Indemnification</p>
<p>1.7 Termination</p>
<p>1.8 Miscellaneous (including changes to the terms)</p>
<p>1.9 Applicable law and Disputes</p>

<h2>1.1 The Use of TVB Services</h2>
<p>
  Subject to the Terms and Conditions, TVB grants each User a limited, non-exclusive, non-transferable and revocable
  license to use the TVB Services - in particular, each User should be aware of the following rules when using the TVB
  Services:
</p>
<p>
  <span class="text-underline">You will not violate any laws</span>
  applicable to you, nor the rules laid out in the Terms and Conditions, when using the TVB Services. You will refrain
  from infringement of any intellectual property rights, personal rights, fundamental rights, etc. You will obtain any
  permits or licenses which are required for you to use the TVB Services, and if you act like a Seller, you will not
  sell anything that violates any laws. Further, you will not engage in fraud, theft, anti-competitive or threatening
  behavior or similar unlawful acts against TVB, other Users or any third party. Users are made aware that their
  behaviour on the Platform is their sole responsibility and for which they may be held liable.
  <span class="text-underline">TVB is not responsible for the behaviour of any Users of the Platform</span>
  .
</p>
<p>
  <span class="text-underline">You will not steal from TVB.</span>
  You agree not to steal, reverse engineer or otherwise try to obtain the source code of the Platform. Further you will
  respect and not infringe any intellectual property belonging to TVB (i.e. trademarks, the name ‘The Vintage Bar’, the
  TVB logo or other marks, slogans or designs used in connection with the TVB Services). Please note that all content on
  the Platform is protected by copyright, trademark and/or patent laws, and that any reproduction of all or parts of
  such content will be considered an infringement of the intellectual property rights of TVB and/or its licensors.
</p>
<p>
  <span class="text-underline">You will pay your bills.</span>
  It is your responsibility to pay all sums owed by you to TVB, other Users or any third party. This also means that you
  are solely responsible for collecting and/or paying any applicable taxes (including VAT) for any purchases or sales
  you make when using the TVB Services.
</p>
<p>
  <span class="text-underline">You will act honest and fair.</span>
  When engaging with TVB, other Users or third parties by use of the TVB Services you will act in honesty, be polite,
  moderate and respectful. TVB reserves the right to moderate the Platform when notified of behaviour which in TVB’s
  opinion violates the Terms and Conditions.
</p>
<p>
  <span class="text-underline">You agree that TVB may contact you online</span>
  via the Platform or by email, when we have a need to communicate certain legal information to you. If we require you
  acceptance to certain legal matters, you further agree that your electronic acceptance shall be as binding as if you
  had signed a paper in person.
</p>

<h2>1.2 Having an Account with TVB</h2>
<p>
  Some of the TVB Services are only available to Users who create an account. Users who create an account acknowledge
  and agree, that:
</p>
<p>
  <span class="text-underline">You must be at least 18 years old and have legal capacity to enter into sales.</span>
  Minors under 18 years but at least 15 years may use the Platform through an account owned by their legal guardian,
  with such person’s permission and under such person’s direct supervision. In such cases both the minor and the legal
  guardian will be deemed a User as defined herein.
</p>
<p>
  <span class="text-underline">You must provide accurate and valid information</span>
  , and must choose an appropriate account name (remember that account names must not infringe any intellectual property
  rights.
</p>
<p>
  <span class="text-underline">You are solely responsible for your own account.</span>
  Accounts are personal. Each User is solely responsible for any and all activity conducted via such User’s account and
  shall be liable for any behavior including any trades conducted via such Users account. Users should protect their
  account details to avoid others from gaining access to the Users account. If the User is a legal entity, the person
  setting up the account guarantees that they have the authority to agree to the Terms and Conditions and to enter into
  transactions in the name of such legal entity. Accounts are non-transferable.
</p>
<p>
  The fact that a User has created an account with TVB shall not be interpreted as to TVB and the User having created an
  agency, partnership, joint venture, employment relationship or franchise relationship.
</p>
<p>To set up an account please visit the main page.</p>
<p>
  <span class="text-underline">You must inform TVB about any suspicion of fraudulent use of your account.</span>
  Users who believe their account name, identity, password or other account details are being fraudulently used,
  undertake to inform TVB by email to Contact@thevintagebar.com as soon as they become aware.
</p>

<h2>1.3 User Content</h2>
<p>
  Users having set up an account may create and post content (i.e. User Content) on the Platform, such content may
  include shop name, profile pictures, listing photos, listing descriptions, reviews, comments, user names, etc. When
  creating and posting User Content you acknowledge and agree that:
</p>
<p>
  <span class="text-underline">You are responsible for the User Content you put online.</span>
  TVB does not monitor (but only host) User Content before upload to the Platform and each User is therefore solely
  responsible for the User Content created by such User, and represents in favour of TVB that he/she/it has all
  necessary rights to any User Content created by such individual/entity and that the User Content does not infringe or
  violate any third party’s rights.
</p>
<p>
  <span class="text-underline">You will not post inappropriate, false or misleading content.</span>
  Users are expected to act respectfully when using the TVB Services, which includes the fact that Users shall refrain
  from creating and posting any User Content which may be considered abusive, threatening, vulgar, offensive, false,
  misleading or in any other way inappropriate.
</p>
<p>
  <span class="text-underline">
    TVB shall be granted a non-exclusive, worldwide, royalty-free, irrevocable, transferable, sublicensable, perpetual
    license
  </span>
  to use, display, edit, modify, reproduce, distribute, store, and prepare derivative works of User Content created by
  you, and you agree not to assert any moral rights or rights of publicity against TVB for the use of the User Content
  created by you. The license hereby granted enables TVB to offer the TVB Services across various channels and to
  optimize the User Content for such channels and its users. By granting this right to TVB you further acknowledge that
  TVB has a legitimate interest in using your personal information within the scope of the license provided.
</p>
<p>
  If you see any User Content on the Platform which in your opinion infringes intellectual property rights or otherwise
  is in violation of the Terms and Conditions please get in contact by sending an email to
  <a href="mailto:contact@thevintagebar.com" class="link primary">contact@thevintagebar.com</a>
  .
</p>
<p>
  Please note that in order to uphold the Terms and Conditions TVB reserves the right to take appropriate actions e.g.
  to remove infringing User Content, or disable Users access to their account - you will be informed in connection
  herewith.
</p>

<h2>1.4 Privacy</h2>
<p>
  <span class="text-underline">
    Users are responsible for the truthfulness and accuracy of the personal information provided
  </span>
  when using the TVB Services. When using the TVB Services Users agree that TVB may process their personal information
  in accordance with the Privacy Policy.
</p>
<p>
  Please note that both TVB and Sellers process personal information (e.g. buyer name, email and address information),
  consequently TVB and the Sellers are separate and independent data controllers pursuant to applicable EU law. Each
  party is responsible and shall be solely liable for the personal information that it processes. If TVB however is
  found to be jointly liable with a Seller for a breach of any data protection regulation rules for which the Seller is
  responsible, the Seller shall indemnify TVB for any loss incurred in connection herewith.
</p>

<h2>1.5 TVB’s Responsibilities towards Users</h2>
<p>
  <span class="text-underline">TVB offers a market place</span>
  through which Sellers and Buyers may trade Products with each other. TVB does not produce, store or inspect any of the
  Products sold, except where specifically stated. The Products offered via the market place are market, and sold
  directly by independent Sellers, except where specifically stated otherwise. TVB does not give any representation or
  warranties as to the quality or safety or the Products sold by independent Sellers, and therefore Users acknowledge
  and agree that any claim relating to the Products shall be brought directly against the Seller of such Product.
</p>
<p>
  Likewise, all content created by and all activities performed by Users (i.e. User Content) is the sole responsibility
  of the User having created such content or performed such activity. Thus, also set out above in section 1.3, TVB does
  not monitor User Content or User activity before upload to the Platform and consequently TVB does not give any
  representation or warranties as to the truthness, accuracy, copyright compliance, legality or decency of any User
  Content and disclaim responsibility and liability for any activities performed by Users.
</p>
<p>
  <span class="text-underline">You, as a User, agree to release TVB from any claims related to</span>
  the Products sold through the TVB Services and/or related to User Content and/or related to the activity by Users,
  this also includes claims relating to any losses of data, any loss of a sale or purchase opportunity, any loss of
  earnings, of turnover, of business activity, of opportunities or of clients, any damage to the User’s reputation or
  interruptions of their activity, and any indirect loss or damage incurred by the User, or any impediment or delay in
  the performance of its obligations caused by any act or omission on the part of a User (or of any person acting for
  and on the User’s behalf) or arising from force majeure.
</p>
<p>
  <span class="text-underline">Third-party services</span>
  may be needed to use some of TVB’s Services and links to such service providers may be included in the TVB’s Services.
  When Users access such third-party services they do so at their own risk, and any terms and conditions agreed to for
  the use of such services is a matter between the applicable User and the third-party service provider. TVB is not a
  party to such an arrangement and consequently cannot be held responsible for the interaction between the User and a
  third-party service provider.
</p>
<p>
  <span class="text-underline">TVB is providing any TVB services on a “best effort basis”</span>
  , and therefore Users acknowledge and accept that the TVB Services are provided “as is” and without any kind of
  warranty (express or implied), including in relation to the TVB Services fitness for a particular purpose. This also
  means that TVB does not guarantee the availability and/or accessibility of the Platform, nor that the TVB Services
  will be secure or free from defects, errors, virus or other types of malware and/or harmful material. Consequently,
  Users acknowledge and accept that they use the TVB Services at their own risk and that they (to the extent permitted
  by law) release TVB (and for the purpose of this clause any employees or directors of TVB) from any claim related to
  such aforementioned matters, as well as any indirect losses arising from the use of the TVB Services. If a claim is
  brought against TVB, the aggregate liability of TVB towards one User shall not exceed the amount paid by such User to
  the benefit of TVB during the course of the last twelve months.
</p>
<p>
  Please note that the terms set out in this section are not intended to deprive a User from any statutory rights, which
  a User enjoys pursuant to applicable law and from which the User and TVB cannot deviate; in such case the above
  mentioned limitations on TVB’s warranties or limitation on TVB’s liability towards a User shall be interpreted as TVB
  haven limited its warranties and its liability towards such User to the maximum extent possible pursuant to the
  applicable law provisions.
</p>

<h2>1.6 Indemnification</h2>
<p>
  <span class="text-underline">
    You agree to defend and hold TVB (and for the purpose of this clause any employees or directors of TVB) harmless
    from any claim or demand
  </span>
  (including reasonable legal fees and other fees associated with the defend against such claim or demand) arising from
  your acts or omissions as a User. In case TVB gets sued, TVB reserves its right to handle the defense (even though TVB
  is indemnified by the User) against such a claim as TVB sees fit, and the User agrees to cooperate with TVB in good
  faith.
</p>

<h2>1.7 Termination</h2>
<p>
  <span class="text-underline">You may terminate your account at any time;</span>
  this will not affect the availability of some of your User Content, which will be kept for the purpose of TVB
  performing the TVB Services.
</p>
<p>
  <span class="text-underline">TVB may terminate your account</span>
  (including any accounts to which TVB has reasonable grounds to believe, that you are associated). However, TVB will
  only terminate your account if you (or people working together with you) are breaching the Terms and Conditions - in
  which case TVB will inform you. Please note that in such a case the User will not have a legal or contractual right to
  demand access to the TVB Services, and the User may lose any information associated with the closed account.
</p>
<p>
  <span class="text-underline">
    TVB reserves the right to suspend, change or terminate any of the TVB Services for any reason.
  </span>
  TVB will not be liable to any Users for any loss or potential loss such User may incur due to the TVB Services being
  suspended, changed or terminated.
</p>
<p>
  In each case listed above, please note that these Terms and Conditions will remain in force as a contractual
  relationship between the User and TVB.
</p>

<h2>1.8 Miscellaneous</h2>
<p>
  <span class="text-underline">These Terms and Conditions will be subject to changes from time to time.</span>
  In case of material changes, Users will be notified. Changes will as a general rule step in to force 15 days after
  notification. Use of the TVB Services following such date will constitute a Users acceptance of the amended Terms and
  Conditions. Users who have questions, comments or who do not accept the amended Terms and Conditions must contact TVB
  by email to
  <a href="mailto:legal@thevintagebar.com" class="link primary">legal@thevintagebar.com</a>
  .
</p>
<p>
  These Terms and Conditions may be deviated from by explicit agreement between the User and TVB. If any part of the
  Terms and Conditions is found to be unenforceable, that part will be limited to the minimum extent necessary so that
  the Terms and Conditions will otherwise remain in full force and effect. Failure to enforce any part of the Terms and
  Conditions is not a waiver of TVB’s right to later enforce that or any other part of the Terms and Conditions. TVB may
  assign any of its rights and obligations under the Terms and Conditions.
</p>
<p>
  <span class="text-udnerline">
    Users acknowledge and accept that they shall not contact other Users directly outside the Platform
  </span>
  in order to negotiate and/or conclude a transaction of Products. Further Users shall refrain from linking to other
  stores on the Platform or where Products may be sold, without TVB’s prior written consent.
</p>

<h2>1.9 Applicable law and Disputes</h2>
<p>
  The Terms and Conditions are governed by, and shall be construed in accordance with Danish Law (disregarding its
  principles on choice of law). The UN convention on Contracts for the International Sale of Goods shall not apply to
  these Terms and Conditions.
</p>
<p>
  If the parties fail to reach an amicable settlement of any dispute, the User and TVB agrees to submit the dispute to
  the Danish courts. Prior to submitting the dispute to the Danish courts, either party may seek settlement of the
  dispute by mediation arranged by Mediationsinstituttet (The Danish Mediation Institute, www.mediationinstituttet.dk)
  in accordance with the “Rules of Procedure of Mediations instituttet”. If, in the opinion of one or more of the
  parties, a dispute or a disagreement has arisen between them, either party shall be entitled to file a request with
  Mediations instituttet for commencements of mediation proceedings.
</p>
<p>
  For the avoidance of doubt, these Terms and Conditions shall not be construed as to deprive a User any rights
  following from mandatory and non-voidable consumer protection rules of the local laws of the applicable User’s country
  of residence and from which the User and TVB cannot not deviate by way of agreement in these Terms and Conditions.
</p>
<p>
  If you have any questions to the Terms and Conditions, please contact us via:
  <a href="mailto:contact@thevintagebar.com" class="link primary">contact@thevintagebar.com</a>
  .
</p>

<h2>2 Sellers’ Terms (applicable to all Users selling Products)</h2>
<p>
  As a User you may market and sell Products to other Users via the Platform. Users selling Products via the Platform
  are also referred to as “Sellers”. For the avoidance of doubt, a Seller is also considered a User and as such Sellers
  are obliged to accept and act in accordance with both Section 1 (in their capacity as User) and Section 2 (in their
  capacity as Seller) of the Terms and Conditions.
</p>
<p>
  By using any of the TVB Services (including browsing of the Platform) you agree to the Terms and Conditions. If you do
  not agree to the Terms and Conditions you may not use any of the TVB Services. If you have any questions about the
  Terms and Conditions please reach out to: Contact@thevintagebar.com.
</p>
<p>This section include the following information:</p>
<p>2.1 TVB’s responsibilities towards Sellers</p>
<p>2.2 A Seller’s rights and obligations</p>
<p>2.3 Offering Products for sale on the Platform</p>
<p>2.4 Completion of Sales (including delivery)</p>
<p>2.5 Payment and Commission</p>
<p>2.6 Transfer of ownership and risk</p>
<p>2.7 Cancellation of orders and returns</p>
<p>2.8 Miscellaneous (including changes to the terms)</p>
<p>2.9 Applicable law and disputes</p>

<h2>2.1 TVB’s Responsibilities towards Sellers</h2>
<p>
  <span class="text-underline">TVB offers a marketplace.</span>
  TVB acts solely as a platform for Users who wish to trade Products and is not the owner and/or the seller/reseller of
  the Products (except that TVB may in specific cases act as Seller of Products, in which case it will be specifically
  stated in the Product Description) or otherwise an agent or representative of either the seller or the buyer. Users
  selling Products via the Platform act on their own behalf and for their own account. For the avoidance of doubt, TVB
  may assist the Seller in connection with the completion of the sale, but TVB is not a contractual part in the sales
  contract between the selling User (i.e the Seller) and the buying User (i.e. the Buyer), and therefore TVB cannot be
  held liable for any liability arising out of such contractual relationship.
</p>
<p>
  <span class="text-underline">TVB may offer additional services</span>
  to the Buyer or the Seller. This could include (but is not limited to) assistance to the Seller in connection with
  conclusion of the sale, with the delivery of Products to Buyers (see below), or renegotiation of a sales price on
  behalf of the Buyer, in cases where a Product fails the compliance check (see below). TVB’s deliverance of such
  additional services shall not alter TVB’s legal position nor its obligations as set out in this section 2.1 of the
  Terms and Conditions.
</p>
<p>
  <span class="text-underline">TVB’s quality control</span>
  of Products sold by Sellers via the Platform is a check designed to identify if the relevant Product fits the Product
  Description made by the Seller. Consequently, TVB cannot be held liable for the quality of the Products and any claims
  related to Products shall be a matter between the Seller and the Buyer of relevant Product.
</p>
<p>
  <span class="text-underline">Claims in relation to Products must be made to the Seller.</span>
  This includes claims relating to the Product and/or matters relating to exchange or returns of said Products. The
  Seller is solely responsible for the handling of claims relating to such matters and solely liable for any loss
  relating thereto. If TVB decides to assist the Seller in connection with the handling of any claims (e.g. by acting as
  first-point-of-contact for requests from Buyers), the Seller shall compensate TVB for TVB’s assistance in connection
  therewith.
</p>

<h2>2.2 A Seller’s rights and obligations</h2>
<p><span class="text-underline">As a Seller on the Platform you represent and warrant that:</span></p>
<p>
  (i) You are the sole owner of any Products you advertise and list for sale on the Platform via your account, or, you
  have obtained a legal right from the owner of the Product to advertise and list the Product for sale on the Platform
  via your account.
  <br />
  (ii) Your advertisement and listing of the Product on the Platform does not violate any laws applicable to you or the
  Platform, nor does it infringe any third parties right or breach any agreements to which you are a party or have
  adhered.
  <br />
  (iii) Products which you advertise or list on the Platform complies and coincide with the origin, the condition and
  characteristics set out in the Product Description and the Product is in any other aspect true to the description
  under which it is offered for sale and as set out in the Product Description.
  <br />
  (iv) any information you provide to TVB about the Product or set out in the Product Description shall be
  comprehensive, honest and accurate.
  <br />
  (v) You do not offer any Products for sale on the Platform which is a counterfeit, prototype, or products which you
  are not authorised to sell.
  <br />
  (vi) Upon request from TVB you must provide any documentation which TVB may require in order to verify that your
  representations and warranties set out in this Section is true and accurate (in particular that you are authorised to
  sell the Product and that the product is not a counterfeit).
  <br />
  (vii) you are solely responsible for (a) any advertisements made on Platform, (b) any descriptions included in the
  Product Description, (c) any fulfilment of a sale of a Product, and (d) any obligations and/or claims which may be a
  result of your advertisement or sale of a Product via the platform, and you will indemnify TVB for any losses which
  TCB may incur due to your advertisement and/or sale of such Products on the Platform,
  <br />
  (viii) Specifically in respect to Products which turns out to be a counterfeit or a Product whose sale is prohibited,
  you accept and acknowledge that you shall bear the risk that the Product might be seized by the appropriate
  authorities or by the owner of the brand involved, which might impound it or destroy it. Further you accept and
  acknowledge that in such case, it will be your responsibility to retrieve the Product in question at your sole risk
  and expense and you shall compensate TVB for any loss it may incur as a result of the Product being a counterfeit, and
  <br />
  (ix) Lastly, if you are a professional Seller, you agree that you will honour any statutory rights in favour of
  consumers pursuant to applicable law, including but not limited to, a consumer’s right to withdraw from the
  transaction within a certain time period without cause. If you are a professional Seller in accordance with laws
  applicable to you, it is your sole responsibility to inform TVB of the fact that you are a professional Seller. If a
  Product is offered for sale by a professional Seller, this will be highlighted on the product page (including in cases
  where TVB acts as a Seller of Products).
  <br />
</p>
<p><span class="text-underline">As a Seller you further accepts that:</span></p>
<p>
  (i) TVB shall be entitled to (a) alter and/remove Product Descriptions made by you, if TVB has reason to believe that
  said information is incorrect, misleading or fake, (b) alter and/or remove any Products offered for sale by you on the
  Platform (c) suspend your access to the Platform, (d) close and/or delete your account in case TVB becomes aware that
  you have acted in breach of the Terms and Conditions or any other agreements entered into between you and TVB, and/or
  (e) revert any transactions between you (as a Seller) and a Buyer if TVB becomes aware that any of the representations
  set out in item (i) to (ii) or (v) to (ix) turns out to be inaccurate, in each case without you being entitled to
  claim any compensation from TVB, and
  <br />
  (ii) You are responsible for protecting Users’ personal information received or processed, and you must comply with
  all relevant legal requirements. This includes applicable data protection and privacy laws that govern the ways in
  which you can use Users information. These laws may require that you post and comply with your own privacy policy,
  which must be accessible to Users with whom you interact. Your privacy policy must be compatible with these Terms and
  Conditions.
  <br />
</p>

<h2>2.3 Offering of Products for Sale on the Platform</h2>

<h3>2.3.1. Product Description</h3>
<p>
  <span class="text-underline">
    To advertise and list a Product on the Platform, you must create a product page for each Product.
  </span>
  The product page must contain a description of the Product which as a minimum must contain the following information
  (the “Product Description”):
</p>
<p>
  (i) brand;
  <br />
  (ii) origin;
  <br />
  (iii) general characteristics such as color, measurements, age, etc.
  <br />
  (iv) product category and subcategory (e.g jewellery, necklace)
  <br />
  (v) condition, including any faulty workmanship, damage, wear or other attributes which a buyer may be expected to
  reasonably want when considering to buy the Product.
  <br />
  (vi) description of all types of alterations, customisations, personalisations (if any)
  <br />
  (vii) price (together with any extra charges which may apply)
  <br />
  (viii) contact information on the seller
  <br />
  (ix) a minimum of five (5) pictures
  <br />
</p>

<p>
  As a seller of Products, you guarantee that any Products which you list for sale on the Platform are available, and
  that you will remove the listed Products (by deleting the product page including the Product Description) as soon as
  such Products become unavailable for whatever reason.
</p>
<p>
  Please note, that as a Seller you will not be able to alter the Product Description (including the price) after the
  Product Description has been posted online without TVB’s assistance.
</p>
<p>
  TVB may reject or make additional alteration (as solely determined by TVB) of Product Descriptions if the description
  does not comply with the minimum requirements; is inappropriate; is inconsistent with the expression (as determined by
  TVB) on the Platform, or; if TVB has reasonable beliefs that the Product violates any laws or infringe any third party
  rights.
</p>

<h3>2.3.2 Ranking/categorization</h3>
<p>
  Products listed for sale on the Platform are generally displayed and grouped together based on the User’s preference.
  The User may choose to segment the Products by price, relevance or the “most wanted” function:
</p>
<p>
  “Most wanted” items are items selected by our editorial team based on them being especially compelling from a fashion
  perspective and based on current fashion trends.
</p>
<p>“Relevance”: the Products are sorted based on the other products which the User has shown an interest in.</p>
<p>“Least expensive”: the Products are sorted from least to most expensive;</p>
<p>“Most expensive”: the Products are sorted from most to least expensive;</p>
<p>“New in”: the Products are sorted from newest uploaded products to the oldest.</p>

<h2>2.4 Completion of Sales (and Delivery)</h2>
<p>
  <span class="text-underline">When a Buyer has completed an order</span>
  TVB will send the Buyer an order confirmation and notify you (the Seller) of the sale. The email will include a
  pre-paid voucher which you may use to ship the Product to TVB.
</p>
<p>
  <span class="text-underline">
    Unless otherwise specifically agreed between TVB and you, all Products must be sent for transit with TVB in order
    for TVB to complete a compliance check (see below).
  </span>
  TVB will send Products passing the compliance check to the Buyer, on behalf of the Seller. The Seller shall compensate
  TVB for any costs associated with the compliance check and transfer of Products from TVB to the Buyer.
</p>
<p>
  <span class="text-underline">Products must be shipped to TVB as soon as possible</span>
  to ensure that TVB receives the Product no later than 7 days after TVB has sent you the notification email (see
  above). If you are unable to comply with this delivery time schedule you must inform TVB immediately. If you are
  repeatedly unable to comply with the delivery time schedule, TVB may impose a fine of up to EUR 15 per event, payable
  to TVB (as compensation for any extra costs TVB may incur due to the late delivery). If the Seller has not sent the
  Products 7 days after TVB has sent a notification of the sale to the seller, TVB is entitled to cancel the sale of the
  Product.
</p>
<p>
  <span class="text-underline">When preparing the Products for shipment it is your responsibility</span>
  to ensure that the Products are in perfect condition, meaning that the item must be clean folded/packed properly, and
  that the Products are packed securely (to avoid any damages).
</p>
<p>
  If you ship more than one Product in one shipment, you must (i) send a list of Products included in the shipment
  together with the tracking number to: Mab@thevintagebar.com on the same day as the courier picks up the shipment, and
  (ii) include a list of the Products included in the shipment, for TVB to verify the items upon arrival.
</p>
<p>
  If Products received by TVB are not in a condition ready for shipment to the Buyer or a Product on the list is not
  included in the shipment, you shall be liable for any costs incurred by TVB, e.g. in order for the Product to be
  prepared for shipment to the Buyer. Further, TVB disclaims any liability towards you as a seller in case of damages or
  loss of the Products during transit to TVB and until such time as the Product has been delivered at TVB’s address.
  Consequently, in case of damages during shipment you will only be entitled to such compensation as offered by the
  carrier.
</p>
<p>
  <span class="text-underline">
    When shipping the Product you must secure evidence for shipment of the order to TVB.
  </span>
</p>

<h2>2.5 Payment</h2>
<p>
  When the Buyer places an order, he/she/it will be asked to pay the sale price for the sold Product(s) by means of one
  of the payment methods offered from time to time on the Platform.
</p>
<p>
  <span class="text-underline">
    When the Buyer has confirmed receipt of the Product, TVB will transfer the sale price to you
  </span>
  , less Commission (as defined below) and any other amounts which you may owe TVB (i.e. any extra compensation charged
  by TVB as a result of clauses in the Terms and Conditions). TVB will transfer said amount to you 7 days after the
  Buyer’s confirmation of receipt of the Product, unless you are a professional seller, in which case TVB will transfer
  said amount to you 21 days after the Buyer’s confirmation of receipt of the Product. The amount will be paid out to
  the account designated by you in your account details. Please note that the payment may be subject to local
  charges/fees payable to your bank/Payment provider, for which you as a recipient of the payment will be liable. You
  will not be entitled to interests on the amount owed to you by TVB for the period that such money is held on TVB’s
  accounts.
</p>
<p>
  <span class="text-underline">You accept and acknowledge that you must return any amounts received from TVB</span>
  in connection with a sale, within 3 days, if it turns out that your Product was sold in violation with your
  obligations pursuant to the Terms and Conditions (or any specific agreements between you and TVB) and especially if
  sold in violation with the representations and warranties given by you (see section 2.2).
</p>
<p>
  <span class="text-underline">
    If you are a professional, you further accept and acknowledge that you must return any amounts received from TVB
  </span>
  in connection with a sale, within 3 days, if the Buyer exercises its statutory right to withdraw (typically within 14
  days) from the trade entered into with you as a professional seller.
</p>
<p>
  <span class="text-underline">
    Please note that in connection with the above, TVB reserves the right to offset any amount that remains due by you
  </span>
  (as a result of your use of the TVB services) against an amount owed by TVB. If TVB exercises this right, you will be
  informed in connection herewith.
</p>

<h3>2.5.1 Commission</h3>
<p>
  <span class="text-underline">
    TVB is entitled to a commission for the TVB Services offered to you in connection with your sale of Products via the
    Platform
  </span>
  (“Commission”). The Commission is calculated in relation to each sale of a Product and the applicable Commission will
  be set out on the product upload page, for you to see before you decide to upload the Product for sale.
</p>
<p>
  <span class="text-underline">
    The Commission is included in the sale price of the Product as shown on the Platform
  </span>
  . As described in section 2.5 the Commission is deducted in the sale price paid before TVB completes the payment to
  you as a Seller.
</p>

<h2>2.6 Transfer of Ownership and Risk</h2>
<p>
  <span class="text-underline">The risk of loss and/or damages to the Product transfers to Buyer</span>
  as of the Buyer’s receipt of the Product (or by a third party which the Buyer has instructed to accept the Products on
  his/her/its behalf). Up until the transfer of risk of loss and/or damages to the Buyer, you as a Seller bear the risk
  of any damages (including accidental damages).
</p>
<p>
  <span class="text-underline">Title to the Product shall transfer to the Buyer</span>
  when TVB, on behalf of you as the Seller has received full payment (including taxes, delivery costs, etc.) from the
  Buyer.
</p>
<p>
  <span class="text-underline">
    You as a Seller agrees, that if the Product is lost or damaged while in TVB’s custody
  </span>
  (during transit), you shall not be entitled to any compensation, except for the payment which you would otherwise be
  entitled to in accordance with section 2.5 (i.e. the sale price less Commission and less any additional compensation
  owed to TVB). Further, in case of damages during shipment you will not be entitled to compensation from TVB, but only
  be entitled to such compensation as offered by the carrier.
</p>

<h2>2.7 Cancellation of Orders and Returns</h2>

<h3>2.7.1 Failed compliance check</h3>
<p>
  <span class="text-underline">If Products transit with TVB, a compliance check will be conducted by TVB.</span>
  If the compliance check reveals that the Product is not compliant with the Product Description, TVB will inform you
  and the Buyer. The Buyer is entitled to cancel the order of a Product which fails the compliance check, or, try to
  renegotiate the price of the Product.
</p>
<p>
  <span class="text-underline">If a Product fails the compliance check TVB may charge you a fee of</span>
  EUR 15 (the estimated costs incurred by TVB in connection with the handling of the canceled order) plus the shipping
  costs payable by TVB for sending the Product back to you.
</p>

<h3>2.7.2 Returns from the Buyer</h3>
<p>The Buyer may request a cancellation of the order if:</p>
<p>
  (i) The Buyer has not received the Product within 30 days of the date of the order, provided that TVB shall be allowed
  7 days to remedy the late delivery, or
  <br />
  (ii) The Buyer, within 14 days of receipt of the Product, notify TVB that the Product is materially inconsistent with
  the Product Description, and TVB subsequently considers the claim to be justified (after having duly investigated the
  problem).
  <br />
</p>
<p>
  To do so the Buyer must request a cancellation by email to TVB: contact@thevintagebar.com or by contacting the Seller
  (via the Platform). The claim must describe in detail why the Buyer wishes to cancel the order, and in case of (ii)
  above, must include pictures of the problem.
</p>
<p>
  In case the Product needs to be returned to TVB for inspection, TVB will send the Buyer a pre-paid voucher enabling
  the Buyer to return the Product. The cost of the pre-paid voucher delivered by TVB will be deducted from the Buyer’s
  entitled refund. The Buyer must return the Product (with any seals unbroken) within 14 days from the Buyer’s receipt
  of the Product.
</p>
<p>
  If the claim for cancellation of an order is justified, TVB will refund the Buyer by way of the same payment method as
  used by the Buyer when placing the order (unless another refund method is specifically agreed). The refund will be
  completed upon TVB/the Seller’s receipt of the returned Product, or in case of (i) above, after the elapse of the 7
  days in which TVB is allowed to remedy the late delivery.
</p>
<p>
  If it is impossible for TVB to return a canceled order to you (the Seller) TVB will store the Product at your risk and
  expense (and TVB may claim reasonable costs for this service). If you do not claim the Product within 6 months as of
  the date of the annulment of the order, TVB shall become the owner of the unclaimed Product, and may destroy, donate
  or otherwise make use of the Product.
</p>

<h3>2.7.2.1 Special rights of certain consumers acquiring Products from professional sellers.</h3>
<p>
  Consumers resident in a EU-country (“Protected Consumer”) enjoy statutory rights pursuant to applicable law, which
  entitles the consumer to certain protective rights, when entering into transactions with a professional seller. Such
  protective rights includes, (i) a right to withdraw from the transaction (without cause) within a certain time period
  (typically 14 days from the date of the order), (ii) a right to bring claims concerning defects in a prolonged period
  following the date of the order (typically two years from the date of the order).
</p>
<p>
  If you are a professional Seller you accept to honour any statutory rights in favour of Protected Consumers, and to
  live up to any obligations which are incumbent on you as a result of such statutory rights. TVB will use reasonable
  efforts to assist you with the handling of any claims from Protected Consumers, but you shall be solely responsible
  for the fulfilment of any obligations arising as a result of any statutory rights granted in favour of the Protected
  Consumer.
</p>

<h2>2.8 Miscellaneous (including changes to the terms)</h2>
<p>
  Please refer to section 1.6 to 1.8 of the Terms and Conditions which applies between you as a User (and as a Seller)
  and TVB.
</p>

<h2>2.9 Applicable law and Disputes</h2>
<p>
  Please refer to Section 1.9 of the Terms and Conditions which applies between you as a User (and as a Seller) and TVB
</p>

<h2>3 Buyers’ Terms (applicable to all Users buying Products)</h2>
<p>
  As a User you may use the Platform to enter into transactions for the purpose of acquiring Products from other Users
  via the Platform. Users acquiring Products via the Platform are also referred to as “Buyers”. For the avoidance of
  doubt, a Buyer is also considered a User and as such Buyers are obliged to accept and act in accordance with both
  Section 1 (in their capacity as User) and Section 3 (in their capacity as buyer) of the Terms and Conditions.
</p>
<p>
  By using any of the TVB Services (including browsing of the Platform) you agree to the Terms and Conditions. If you do
  not agree to the Terms and Conditions you may not use any of the TVB Services. If you have any questions to the Terms
  and Conditions please reach out to: Contact@thevintagebar.com.
</p>
<p>This section include the following information:</p>
<p>
  3.1 TVB’s responsibilities towards Buyers
  <br />
  3.2 Placing orders via the Platform
  <br />
  3.3 Delivery
  <br />
  3.4 Transfer of Ownership and Risk
  <br />
  3.5 Cancellation of Orders and Returns (Including Protected Customers right to withdraw)
  <br />
  3.6 Miscellaneous (including changes to the terms)
  <br />
  3.7 Applicable law and Disputes
  <br />
</p>

<h2>3.1 TVB’s Responsibilities towards Buyers</h2>
<p>
  <span class="text-underline">TVB offers a marketplace</span>
  (see also Section 1.5). TVB acts solely as a platform for Users who wish to trade Products and is not the owner and/or
  the seller/reseller of the Products (except that TVB may in specific cases act as Seller of Products, in which case it
  will be specifically stated in the Product Description) or otherwise an agent or representative of either the seller
  or the buyer. Users selling Products via the Platform act on their own behalf and for their own account. For the
  avoidance of doubt, TVB may assist the Seller in connection with the completion of the sale, but TVB is not a
  contractual part in the sales contract between the selling User (i.e the Seller) and the buying User (i.e. the Buyer),
  and therefore TVB cannot be held liable for any liability arising out of such contractual relationship.
</p>
<p>
  <span class="text-underline">TVB may offer additional services</span>
  to the Buyer or the Seller. This could include (but is not limited to) assistance to the Seller in connection with the
  delivery of Products to Buyers (see below), or renegotiation of a sales price on behalf of the Buyer, in cases where a
  Product fails the compliance check (see below). TVB’s deliverance of such additional services shall not alter TVB’s
  legal position nor its obligations as set out in this section of the Terms and Conditions.
</p>
<p>
  <span class="text-underline">TVB’s quality control</span>
  of Products sold by Sellers via the Platform is a check designed to identify if the relevant Product fits the Product
  Description made by the Seller. Consequently, TVB cannot be held liable for the quality of the Products and any claims
  related to Products shall be a matter between the Seller and the Buyer of relevant Product.
</p>
<p>
  <span class="text-underline">Claims in relation to Products must be made to the Seller.</span>
  This includes claims relating to the Product and/or matters relating to exchange or returns of said Products. The
  Seller is solely responsible for the handling of claims relating to such matters and solely liable for any loss
  relating thereto. If TVB decides to assist the Seller in connection with the handling of any claims (e.g. by acting as
  first point of contact for requests from Buyers), the Seller shall indemnify TVB for any costs TVB incur in connection
  therewith.
</p>

<h2>3.2 Placing orders via the Platform</h2>
<p>
  <span class="text-underline">As a Buyer you may order Products via the Platform offered for sale by Sellers</span>
  . Products are sold ‘as is’ and in accordance with the Product Description made by the Seller in relation to each
  Product (note that in some cases the Seller may be TVB itself).
</p>
<p>
  <span class="text-underline">Sellers on the platform may either be private Sellers or professional Sellers</span>
  . As a Buyer you are made aware that your rights may differ depending on which type of Seller you are entering into a
  transaction with (e.g. some Buyers may have certain statutory rights, when entering into a transaction with a
  professional seller, see section 3.5). If a Seller of a Product is a professional Seller, this will be explicitly
  stated on the product page containing information about the Product.
</p>
<p>
  <span class="text-underline">
    The prices stated on the Platform in relation to the Products include VAT and taxes applied in the sellers country
    and in Denmark (in case the product transit via TVB for quality check) but do not include any additional costs, such
    as costs associated with shipping, custom duties, VAT or the like which may be applied when the product is imported
    to your country of residence (provided you live outside EU)
  </span>
  . Please be aware that any such additional cost are calculated and charged by the courier who import it to your
  country and therefore we cannot provide you with a calculation of such costs in connection with your purchase. If you
  want to know the cost beforehand, please reach out to the courier you intend to use in connection with your purchase.
</p>
<p>
  <span class="text-underline">Your order may be cancelled by TVB</span>
  in case the Product is not received by TVB from the Seller, or the Product does not pass TVB’s compliance check. In
  each case TVB will notify you. Further, TVB may reject your order, if you are party to an outstanding dispute relating
  to a transaction made via the Platform. You accept that in each such a situation, you shall have no right to
  compensation from TVB.
</p>
<p>
  <span class="text-underline">
    It is your duty to pay the sale price and any related costs for the Product which you acquire via the Platform
  </span>
  . Together with the sale price this includes shipping costs, and may include VAT, custom duties, import taxes or
  similar, which is applied due to your location or the location of the Seller, etc. You must pay the said costs by
  means of one of the payment methods offered from time to time on the Platform. You may be asked, if you want to save
  your payment details; if you accept, this means that the applicable payment provider acting as a service provider to
  TVB may store your financial information. In such a case, please note that TVB’s payment provider will act as the data
  controller in relation to the provided financial data (and not TVB). Please refer to our Privacy Policy for further
  details.
</p>
<p>
  <span class="text-underline">
    Upon completion of an order, and the receipt of your payment TVB will notify you and the Seller.
  </span>
</p>

<h2>3.3 Delivery</h2>
<p>
  <span class="text-underline">
    Following your placement of the order, you may expect to receive your Product within 30 days as of the date on the
    order confirmation, provided that TVB shall be allowed 7 days to remedy any late delivery
  </span>
  . The Seller will ship the Product to TVB who will conduct a compliance check to assure that the Product complies with
  the Product Description, before the Product is redirected to you.
</p>
<p>
  <span class="text-underline">If a product - following shipment to you - is returned to the Seller or TVB</span>
  due to the fact that the carrier was unable to deliver the Product to you, TVB will notify you (the buyer) hereof. The
  Product shall be stored with TVB at your risk and expense (and TVB may claim reasonable costs for this service). If
  you do not claim the Product within 6 months as of the date on the order confirmation, TVB shall become the owner of
  the unclaimed Product, and may destroy, donate or otherwise make use of the Product.
</p>
<p>
  <span class="text-underline">Upon receipt of the Product</span>
  you must check that everything matches the order confirmation, and if this is not the case contact TVB within 72 hours
  of receipt of the package.
</p>

<h2>3.4 Transfer of Ownership and Risk</h2>
<p>
  <span class="text-underline">The risk of loss and/or damages to the Product transfers to you as a Buyer</span>
  as of your receipt of the Product (or by a third party which you have instructed to accept the Products on your
  behalf). Up until the transfer of risk of loss and/or damages to you, the Seller bears the risk of any damages
  (including accidental damages).
</p>
<p>
  <span class="text-underline">Title to the Product shall transfer to you as a buyer</span>
  when TVB, on behalf of the Seller (or on its own behalf, if TVB in a specific situation act as seller of Products) has
  received full payment (including taxes, delivery costs, etc.) from you.
</p>

<h2>3.5 Cancellation of Orders and Returns</h2>
<h3>3.5.1 Failed compliance check</h3>
<p>
  <span class="text-underline">If Products transit with TVB, a compliance check will be conducted by TVB</span>
  . If the compliance check reveals that the Product is not compliant with the Product Description, TVB will inform you
  and the Seller. You are entitled to cancel the order of a Product which fails the compliance check, or, try to
  renegotiate the price of the Product.
</p>

<h3>3.5.2 Returns from the Buyer</h3>
<p><span class="text-underline">You, as a Buyer, may request a cancellation of the order if:</span></p>

<p>
  (i) The Buyer has not received the Product within 30 days of the date of the order, provided that TVB shall be allowed
  7 days to remedy the late delivery, or
  <br />
  (ii) The Buyer, within 14 days of receipt of the Product, notify TVB that the Product is materially inconsistent with
  the Product Description, and TVB subsequently considers the claim to be justified (after having duly investigated the
  problem).
  <br />
</p>

<p>
  To do so the Buyer must request a cancellation by email to TVB:
  <a href="mailto:contact@thevintagebar.com" class="link primary">contact@thevintagebar.com</a>
  or by contacting the Seller (via the Platform). The claim must describe in detail why the Buyer wishes to cancel the
  order, and in case of (ii) above, must include pictures of the problem.
</p>
<p>
  <span class="text-underline">In case the Product needs to be returned to TVB</span>
  , TVB will send you a pre-paid voucher enabling you to return the Product. The cost of the pre-paid voucher delivered
  by TVB will be deducted from your entitled refund.You must return the Product (with any seals unbroken) in time for it
  to be received at TVB no later than 14 days from the time you have utilized the return option via your account or
  otherwise received a return label receipt of the Product (or a third party’s, which you have instructed to accept the
  Products on your behalf, receipt of the Product). TVB disclaims any liability in case of loss or damages of a Product
  while in transit.
</p>
<p>
  <span class="text-underline">
    When shipping the Product you must secure evidence of shipment of the order to TVB/the Seller.
  </span>
</p>
<p>
  <span class="text-underline">
    It is your responsibility to protect and take care of the Product while in your possession
  </span>
  . If the Product are damaged or depreciated in value due to the handling of the Product, TVB shall be allowed to
  reject the return or to withhold an amount equal to the estimated depreciation in value, and if damaged to such extent
  that the Product cannot be offered for sale, to withhold the full amount (in which case you as a Buyer will not
  receive a refund).
</p>
<p>
  <span class="text-underline">
    If the request for cancellation is accepted (following TVB’s quality check of the Product upon receipt), TVB will
    provide you with a refund
  </span>
  by way of the same payment method as used by you when placing the order (unless another refund method is specifically
  agreed). The refund will be completed upon TVB/the Seller’s receipt of the Product, or in case of (i) above, after the
  elapse of the 7 days in which TVB is allowed to remedy the late delivery.
</p>

<h3>3.5.2.1 Special rights of certain consumers acquiring Products from professional sellers.</h3>
<p>
  Consumers resident in a EU-country (“Protected Consumer”) enjoy statutory rights pursuant to applicable law, which
  entitles the consumer to certain protective rights, when entering into transactions with a professional seller. Such
  protective rights includes, (i) a right to withdraw from the transaction (without cause) within a certain time period
  (typically 14 days from the date of the order), (ii) a right to bring claims concerning defects in a prolonged period
  following the date of the order (typically two years from the date of the order.
</p>
<p>
  Each professional Seller is required to accept to honour any statutory rights in favour of Protected Consumers, and to
  live up to any obligations which are incumbent on the Seller as a result of such statutory rights. TVB will use
  reasonable efforts to assist the professional Seller with the handling of any claims from Protected Consumers, but the
  professional Seller shall be the solely responsible for the fulfilment of any obligations arising as a result of any
  statutory rights granted in favour of the Protected Consumer.
</p>
<p>
  If you are a Protected Customer, please note that you will not be able to withdraw from a transaction if the
  applicable Product contains a hygienic seal and such seal is broken upon TVB/the Sellers receipt of the returned
  Product.
</p>
<p>
  Any Protected Customer who wishes to cancel an order or make a claim, must follow the cancellation procedure described
  in section 3.5.2 Or use the standard form attached as Schedule 1 to the Terms and Conditions. If you are Protected
  Customers please note that your claim should be directed to the professional Seller, as TVB is not a party to the
  sales contract between the professional Seller and the Protected Customer.
</p>
<p>
  For further information about your right to withdraw from the transaction as a Protected Customer, please refer to:
  https://www.retsinformation.dk/eli/lta/2013/1457 (available in Danish, but may be translated by use of services such
  as Google Translate). If you want to submit a complaint about the Seller, this may be done to (if you are resident in
  Denmark):
</p>
<p>
  Center for Klageløsning
  <br />
  Nævnenes Hus
  <br />
  Toldboden 2
  <br />
  88000 Viborg
  <br />
  Link: https://www.forbrug.dk/
  <br />
</p>
<p>
  (or if you are a Protected Customer resident in another EU country, you can use the European Commission’s online
  Dispute Resolution Portal: https://ec.europa.eu/info/live-work-travel-eu/consumers_en)
</p>

<h3>3.5.2.2 Ex-gratia gesture granted in favour of Buyers acquiring Products from Non-Professional Sellers</h3>
<p>
  <span class="text-underline">If you have acquired a Product from a non-professional Seller</span>
  , you do not have the right to withdraw from the transaction, however, as an ex-gratia gesture TVB will allow you to
  try to resell the Product via the Platform free of any Commission (see section 2.5.1), but subject to a handling fee
  of 10% of the reselling price, provided that you upload the Product for sale within 14 days as of your receipt of the
  Product. Except for the obligation to pay Commission, you, as a ‘reseller’, will be subject to the same terms and
  conditions as applied to Sellers and as set out in section 2 of the Terms and Conditions.
</p>

<h2>3.6 Miscellaneous</h2>
<p>
  Please refer to section 1.6 to 1.8 of the Terms and Conditions which applies between you as a User (and as a Buyer)
  and TVB.
</p>

<h2>3.7 Applicable law and Disputes</h2>
<p>
  Please refer to Section 1.9 of the Terms and Conditions which applies between you as a User (and as a Buyer) and TVB.
</p>

<p>This document was last updated on 25 July 2023.</p>
